/**
 * 侧边导航
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Menu } from '@tencent/spaui';
import _ from 'lodash';
import globalStore from '@/stores/index';
import * as pathToRegexp from 'path-to-regexp';
import BreathingDot from '@/common-components/breathing-dot';
import './assets/less/index.scoped.less';

/** 菜单项（与 sitemap 中 pathMenuNavMap 项结构一致） */
export interface MenuNavItem {
  key: string;
  url?: string;
  path?: string;
  data?: MenuNavItem[];
  match?: (path: string) => boolean;
  [key: string]: any;
}

export interface MenuNavProps {
  /**
   * 页面地址
   */
  href?: string;
  /**
   * 菜单数据，传入时优先于 account.siteMap.pathMenuNavMap
   */
  data?: MenuNavItem[];
  /**
   * 侧边栏底部追加内容
   */
  footer?: React.ReactNode;
  [key: string]: any;
}

const supportSwitchList = ['/agency/advertiser-list/:type', '/agency/advertising/:type', '/agency/finance-info/:type', '/agency/finance-record/:type', '/agency/finance-credit/:type'];

const ArrowComponent = (props: any) => {
  const { onClick } = props;
  return <span className="menu-nav-arrow" onClick={onClick}></span>;
};

const LinkComponent = (props: any) => {
  const { menuComponentProps, itemProps, ...rest } = props;
  const showBreathingLight = itemProps?.nodeData?.breathingLight?.(globalStore.account) || false;
  if (showBreathingLight) {
    const { children, ...restProps } = rest;
    const newChildren = children.map((v: any) => {
      if (v?.props?.className?.includes?.('spaui-menu-text')) {
        const textChildren = v.props.children;
        return React.createElement(
          'span',
          v.props,
          React.createElement(
            BreathingDot,
            {
              show: true,
            },
            textChildren,
          ),
        );
      }
      return v;
    });
    return <a {...restProps}>{newChildren}</a>;
  }
  return <a {...rest} />;
};

export const MenuNav: React.FC<MenuNavProps> = (props) => {
  const { href = '', data: dataProp, footer, ...otherProps } = props;
  const account: any = globalStore.account || {};
  const history: any = globalStore.history || {};
  const pathname = href || history?.location?.pathname;
  const { siteMap = {} } = account || {};
  const currentMenuNav: MenuNavItem[] = Array.isArray(dataProp) && dataProp.length > 0 ? dataProp : siteMap.pathMenuNavMap?.[pathname] || [];

  const [value, setValue] = useState(pathname);
  // 默认展开全部
  const [open, setOpen] = useState(() => currentMenuNav.map(({ key }: any) => key));
  // 记录 Menu 展开/收起状态，用于控制 footer 仅在展开态显示
  const [menuType, setMenuType] = useState<'normal' | 'mini'>('normal');

  const onTypeChange = useCallback((_e: any, type: 'normal' | 'mini') => {
    setMenuType(type);
  }, []);

  useEffect(() => {
    setOpen(currentMenuNav.map(({ key }: any) => key));
  }, [pathname]);

  const setActiveKey = useCallback((menu) => {
    if (menu?.length) {
      menu.forEach((item: any) => {
        if (item.data?.length) {
          setActiveKey(item.data);
        } else if (item.match && item.match(pathname)) {
          setValue(item.key);
        }
      });
    }
  }, [pathname]);

  useEffect(() => {
    setActiveKey(currentMenuNav);
  }, [currentMenuNav, setActiveKey]);

  const onChange = useCallback((e, value) => {
    if (!value.url && !value.path) {
      return;
    }
    // 菜单项标记了 isExternal（站外独立系统）时，走原生 window.open，
    // 不经 react-router 的 history.push，避免绝对地址被当作路由 path 拼上 basename /ams
    if (value.isExternal) {
      window.open(value.url, value.target || '_blank');
      return;
    }
    const currentHref = pathname;
    let currentType = '';
    let targetHref = '';
    let haveTargeHref = false;


    supportSwitchList.forEach((v) => {
      // 生成正则
      const match = pathToRegexp.match(v, { decode: decodeURIComponent });
      // 匹配当前页href，提取type
      const currentResult: any = match(currentHref);
      if (currentType === '' && Boolean(currentResult)) {
        currentType = currentResult.params.type;
      }
      // 匹配目标页href
      const targetResult = match(value);
      if (targetHref === '' && Boolean(targetResult)) {
        targetHref = v;
      }
    });

    // 如果当前页和目标页同时满足自动跳转，则填充目标页href
    if (currentType !== '' && targetHref !== '') {
      const compile = pathToRegexp.compile(targetHref, { encode: encodeURIComponent });
      targetHref = compile({ type: currentType });
      haveTargeHref = _.filter(siteMap.routes, (v) => v.href === targetHref).length > 0;
    }

    setValue(value.key);
    if (targetHref !== '' && haveTargeHref) {
      history?.push(targetHref);
    } else {
      history?.push(value.url);
    }
    // 滚动到顶部，以防菜单位置太靠下
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const onOpen = useCallback((e, value) => {
    setOpen(value);
  }, []);

  if (!currentMenuNav.length) {
    return null;
  }

  return (
    <Menu
      width={190}
      toggle={true}
      thirdLevelAsPop={false}
      value={value}
      open={open}
      data={currentMenuNav}
      onChange={onChange}
      onOpen={onOpen}
      className="menu-nav"
      LinkComponent={LinkComponent}
      ArrowComponent={ArrowComponent}
      onTypeChange={onTypeChange}
      FooterComponent={footer && menuType === 'normal' ? () => <div className="menu-nav-footer">{footer}</div> : undefined}
      {...otherProps}
    />
  );
};

export default MenuNav;
